import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../../src/api/client";
import aluzonLogo from "../../src/assets/ALUzon Logo.png";

const LINK_LABELS = {
  dashboard: "Dashboard",
  members: "User Management",
  approvals: "Approvals",
  security: "ID Card",
  tickets: "Requests / Tickets",
  events: "Events",
  venues: "Venues",
  bookings: "Bookings",
  reports: "Reports",
  audit: "Audit Log",
  settings: "Settings",
};

const sanitizeEnvText = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }
  const normalized = value.toString().replace(/^['"]|['"]$/g, "").trim();
  return normalized.length ? normalized : fallback;
};

const readStoredAdminUser = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem("adminUser");
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("Failed to parse admin user from storage", error);
    return null;
  }
};

const buildAdminDisplayName = (user) => {
  if (!user) {
    return null;
  }
  const firstName = user.firstName ?? user?.admin?.firstName ?? "";
  const lastName = user.lastName ?? user?.admin?.lastName ?? "";
  const normalized = [firstName, lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  return normalized || user.username || user.email || null;
};

const resolveAdminId = (user) => {
  if (!user) {
    return null;
  }
  const candidateIds = [user.id, user.adminId, user?.admin?.id]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return candidateIds[0] ?? null;
};

export default function AdminFooter({ navItems, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultAdminName = sanitizeEnvText(process.env.REACT_APP_DEFAULT_ADMIN_NAME, "Admin User");
  const appVersion = sanitizeEnvText(process.env.REACT_APP_ADMIN_VERSION, "v1.2.0");
  const supportEmail = sanitizeEnvText(process.env.REACT_APP_SUPPORT_EMAIL, "support@alu.com");

  const links = Array.isArray(navItems)
    ? navItems.filter((item) => Object.keys(LINK_LABELS).includes(item.id))
    : [];

  const handleLogout = async () => {
    const storedUser = readStoredAdminUser();
    const actorId = resolveAdminId(storedUser);
    const actorName = buildAdminDisplayName(storedUser) ?? defaultAdminName;

    try {
      await client.post("/api/admin/audit/logs", {
        action: "admin_logout",
        entityType: "auth",
        entityId: actorId ?? null,
        actorAdminId: actorId ?? null,
        metadata: {
          actorName,
          actorType: "admin",
          status: "logged_out",
        },
      });
    } catch (auditError) {
      console.error("Unable to record logout audit entry", auditError);
    } finally {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      delete client.defaults.headers.common.Authorization;
      navigate("/admin/login");
    }
  };

  return (
    <footer className="admin-footer" role="contentinfo">
      <div className="admin-footer__inner">
        <div className="admin-footer__brand">
          <div className="admin-footer__brand-mark">
            <img src={aluzonLogo} alt="LaborConnect" className="admin-footer__logo" />
          </div>
          <div className="admin-footer__brand-copy">
            <h4>LaborConnect</h4>
            <p>Admin Platform</p>
            <span className="admin-footer__tag">Secure Workspace</span>
          </div>
        </div>
        <div className="admin-footer__links-wrap">
          <p className="admin-footer__links-title">Quick Access</p>
          <nav className="admin-footer__links" aria-label="Footer navigation">
            {links.map((link) => {
              const label = LINK_LABELS[link.id] || link.label;
              const isActive = location.pathname.includes(`/admin/${link.path}`);
              return (
                <button
                  key={link.id}
                  type="button"
                  className={`admin-footer__link${isActive ? " is-active" : ""}`}
                  onClick={() => (onNavigate ? onNavigate(link.id) : navigate(`/admin/${link.path}`))}
                >
                  {label}
                </button>
              );
            })}
            <button type="button" className="admin-footer__link" onClick={handleLogout}>
              Logout
            </button>
          </nav>
        </div>
        <div className="admin-footer__meta">
          <span className="admin-footer__meta-item">Secure environment · {appVersion}</span>
          <a className="admin-footer__meta-link" href={`mailto:${supportEmail}`}>
            {supportEmail}
          </a>
        </div>
      </div>
      <div className="admin-footer__bottom">
        <span>Copyright (c) {new Date().getFullYear()} LaborConnect</span>
        <span>All rights reserved.</span>
      </div>
    </footer>
  );
}

AdminFooter.propTypes = {
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      path: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  onNavigate: PropTypes.func,
};

AdminFooter.defaultProps = {
  navItems: [],
  onNavigate: null,
};
